import { useCallback } from 'react';

import { mapAnnotationForGeneration } from 'src/data/annotation';
import { getAssetTypeMaxCopyLength, shouldAssetCheckCopyLength } from 'src/data/asset';
import { AI_RECOMPOSE_MODE, GENERATION_GUIDANCE_TYPE } from 'src/data/generation';
import {
  doesToolSupportCampaignKeywords,
  doesToolSupportEnhancedImprovements,
  getToolDefaultAssetType,
  isRewriteGenerationTool,
  shouldDisableTOVModes,
} from 'src/data/generationTool';
import { KEYWORDS_SEPARATOR } from 'src/data/keywords';
import { mapScoringMethodForMutation } from 'src/data/scoring';
import { getToneOfVoiceForGeneration } from 'src/data/toneOfVoice';
import { useIsCustomerStarterOrTrialPlan } from 'src/services';
import { useBrandTermsActions, useProjectActions } from 'src/store';

import { MANUAL_SUGGEST_IMPROVEMENT } from '../../../const';
import { useDocumentViewActions } from '../../../store';
import { useLocalActions } from '../store';

const onlyCampaignKeywords = ({ type }) => type === GENERATION_GUIDANCE_TYPE.campaignKeyword;
const withoutCampaignKeywords = ({ type }) => type !== GENERATION_GUIDANCE_TYPE.campaignKeyword;

export const useCollectRecomposeParams = (applyImproveScoreForBasicStarterUser) => {
  const isCustomerStarterOrTrialPlan = useIsCustomerStarterOrTrialPlan();
  const projectActions = useProjectActions();
  const documentViewActions = useDocumentViewActions();
  const brandTermsActions = useBrandTermsActions();
  const localActions = useLocalActions();

  return useCallback(() => {
    const generationTool = localActions.getGenerationTool();

    const getKeywords = () => {
      const annotations = localActions.getAnnotationsForGeneration();
      const relevantAnnotations = [];
      if (isRewriteGenerationTool(generationTool)) {
        const nonCampaignKeywords = annotations.filter(withoutCampaignKeywords);
        relevantAnnotations.push(...nonCampaignKeywords);
      }
      const prepared = relevantAnnotations.map(mapAnnotationForGeneration);
      return prepared.map(({ content }) => content).join(KEYWORDS_SEPARATOR);
    };

    const getCampaignKeywords = () => {
      const annotations = localActions.getAnnotationsForGeneration();
      const relevantAnnotations = [];

      if (isRewriteGenerationTool(generationTool)) {
        const nonCampaignKeywords = annotations.filter(withoutCampaignKeywords);
        relevantAnnotations.push(...nonCampaignKeywords);
      }

      const toolSupportsCampaignKeywords = doesToolSupportCampaignKeywords(generationTool);
      if (toolSupportsCampaignKeywords) {
        const campaignKeywords = annotations.filter(onlyCampaignKeywords);
        relevantAnnotations.push(...campaignKeywords);
      }

      return relevantAnnotations.map(mapAnnotationForGeneration);
    };

    const getForceZeroScore = ({ rewriteText, generationTool }) => {
      const assetType = getToolDefaultAssetType(generationTool);
      const textLengthMax = getAssetTypeMaxCopyLength(assetType);
      const checkCopyLength = shouldAssetCheckCopyLength(assetType);
      return checkCopyLength && rewriteText.length > textLengthMax;
    };

    const getOnlyImproveScore = () => {
      if (isCustomerStarterOrTrialPlan && !applyImproveScoreForBasicStarterUser) {
        return false;
      }

      if (doesToolSupportEnhancedImprovements(generationTool)) {
        const selectedInstructionsOption = localActions.getSelectedInstructionsOption();

        const isManualSuggestImprovement =
          selectedInstructionsOption === MANUAL_SUGGEST_IMPROVEMENT;
        const isTovMode = selectedInstructionsOption === AI_RECOMPOSE_MODE.tov;

        localActions.setIsOnlyImproveScore(!isTovMode && !isManualSuggestImprovement);

        return selectedInstructionsOption !== MANUAL_SUGGEST_IMPROVEMENT;
      }

      return false;
    };

    // Modes
    const modeId = localActions.getGenerationModeId();
    const modeType = localActions.getGenerationModeType();

    // Brief and text content
    const briefContent = localActions.getBriefContent();
    const productName = localActions.getRewriteProductName();
    const rewriteText = localActions.getRewriteText();

    // Keywords/annotations, persona, scoring method
    const keywords = getKeywords();
    const campaignKeywords = getCampaignKeywords();
    const persona = localActions.getPersonaForGeneration();
    const scoringMethod = documentViewActions.getScoringMethod();

    const rephraseContext = briefContent ? JSON.stringify(briefContent) : null;

    // Collect all params in one object
    const params = {
      generationTool,
      rephraseContext,
      productName,
      text: rewriteText,
      recomposeMode: modeId,
      modeId,
      modeType,
      keywords,
      annotations: campaignKeywords,
      customerPersonaId: persona?.id,
      customerPersonaName: persona?.name,
      numberOfVariations: localActions.getNumberOfVariations(),
      language: localActions.getLanguageForGeneration(),
      languageTargetFormality: localActions.getLanguageTargetFormality(),
      onlyImproveScore: getOnlyImproveScore(),
      manualInstructions: localActions.getRewriteInstructions(),
      scoringMethod: mapScoringMethodForMutation(scoringMethod),
      forceZeroScore: getForceZeroScore({ rewriteText, generationTool }),
    };

    const selectedInstructionsOption = localActions.getSelectedInstructionsOption();

    if (
      !shouldDisableTOVModes(generationTool) &&
      selectedInstructionsOption === AI_RECOMPOSE_MODE.tov
    ) {
      const selectedTov = projectActions.getSelectedTov();
      const draftTov = projectActions.getDraftToneOfVoice();
      params.tovInput = getToneOfVoiceForGeneration(selectedTov, draftTov);
      params.recomposeMode = AI_RECOMPOSE_MODE.tov;
      params.onlyImproveScore = false;
    }

    params.brandVoiceIdsForBrandTerms = brandTermsActions.getBrandRulesEnabled()
      ? brandTermsActions.getBrandVoiceIdsForBrandTerms()
      : [];

    return params;
  }, [
    localActions,
    projectActions,
    documentViewActions,
    isCustomerStarterOrTrialPlan,
    applyImproveScoreForBasicStarterUser,
    brandTermsActions,
  ]);
};
